// src/pages/AdminPanel.js
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { signOut } from "firebase/auth";
import { collection, getDocs, doc, setDoc, deleteDoc } from "firebase/firestore";
import "./AdminPanel.css";

function AdminPanel() {
  const navigate = useNavigate();
  const [initialBusySlots, setInitialBusySlots] = useState({});
  const [editedBusySlots, setEditedBusySlots] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Pobieranie danych z Firebase
  useEffect(() => {
    const fetchBusySlots = async () => {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, "kalendarz"));
      const slots = {};
      querySnapshot.forEach((doc) => {
        slots[doc.id] = doc.data().hours;
      });
      setInitialBusySlots(slots);
      setEditedBusySlots(slots); // Inicjalizujemy stan edycji tymi samymi danymi
      setLoading(false);
    };
    fetchBusySlots();
  }, []);

  // Sprawdzanie, czy są niezapisane zmiany
  const hasChanges = useMemo(
    () => JSON.stringify(initialBusySlots) !== JSON.stringify(editedBusySlots),
    [initialBusySlots, editedBusySlots]
  );

  // Funkcja do wylogowania
  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  // Funkcja do zapisu zmian w Firebase
  const handleSaveChanges = async () => {
    setIsSaving(true);
    const promises = [];

    // Przechodzimy przez wszystkie klucze w obu obiektach, aby wykryć wszystkie zmiany
    const allKeys = [
      ...new Set([
        ...Object.keys(initialBusySlots),
        ...Object.keys(editedBusySlots),
      ]),
    ];

    for (const dateKey of allKeys) {
      const initialDaySlots = initialBusySlots[dateKey] || [];
      const editedDaySlots = editedBusySlots[dateKey] || [];

      // Jeśli stany się różnią, wykonujemy operację
      if (JSON.stringify(initialDaySlots) !== JSON.stringify(editedDaySlots)) {
        const dayDocRef = doc(db, "kalendarz", dateKey);
        if (editedDaySlots.length > 0) {
          promises.push(setDoc(dayDocRef, { hours: editedDaySlots }));
        } else {
          promises.push(deleteDoc(dayDocRef));
        }
      }
    }

    await Promise.all(promises);
    setInitialBusySlots(editedBusySlots); // Aktualizujemy stan początkowy
    setIsSaving(false);
    alert("Zmiany zostały zapisane!");
  };

  // Funkcja do anulowania zmian
  const handleCancelChanges = () => {
    setEditedBusySlots(initialBusySlots);
  };

  // Obsługa kliknięcia w termin (teraz modyfikuje tylko stan lokalny)
  const handleSlotClick = (dateKey, hour) => {
    const currentDaySlots = editedBusySlots[dateKey] || [];
    const isBusy = currentDaySlots.includes(hour);
    const newDaySlots = isBusy
      ? currentDaySlots.filter((h) => h !== hour)
      : [...currentDaySlots, hour].sort();

    setEditedBusySlots((prev) => ({ ...prev, [dateKey]: newDaySlots }));
  };

  // Generowanie dni dla wybranego miesiąca
  const generateDaysForMonth = (date) => {
    const month = date.getMonth();
    const year = date.getFullYear();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1));
  };

  const days = generateDaysForMonth(currentMonth);
  const hours = Array.from({ length: 14 }, (_, i) => `${8 + i}:00`);
  const formatDate = (date) => date.toISOString().split("T")[0];

  const goToPreviousMonth = () => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)));
  const goToNextMonth = () => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)));

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <h1>Panel Zarządzania Kalendarzem</h1>
        <div className="admin-actions">
          <button onClick={handleCancelChanges} className="cancel-button" disabled={!hasChanges || isSaving}>
            Anuluj zmiany
          </button>
          <button onClick={handleSaveChanges} className="save-button" disabled={!hasChanges || isSaving}>
            {isSaving ? "Zapisywanie..." : "Zapisz zmiany"}
          </button>
          <button onClick={handleLogout} className="logout-button">
            Wyloguj się
          </button>
        </div>
      </header>
      <main className="admin-content">
        <div className="month-navigator">
          <button onClick={goToPreviousMonth}>&lt; Poprzedni</button>
          <h2>{currentMonth.toLocaleDateString("pl-PL", { month: "long", year: "numeric" })}</h2>
          <button onClick={goToNextMonth}>Następny &gt;</button>
        </div>
        {loading ? (
          <p>Ładowanie kalendarza...</p>
        ) : (
          <div className="admin-calendar-container">
            <div className="admin-calendar-grid" style={{ gridTemplateColumns: `60px repeat(${days.length}, 1fr)` }}>
              <div className="ac-header-cell"></div>
              {days.map((day) => (
                <div key={day} className="ac-header-cell ac-day-header">
                  <div>{day.toLocaleDateString("pl-PL", { weekday: "short" })}</div>
                  <div>{day.getDate()}</div>
                </div>
              ))}
              {hours.map((hour) => (
                <React.Fragment key={hour}>
                  <div className="ac-header-cell ac-hour-header">{hour}</div>
                  {days.map((day) => {
                    const dateKey = formatDate(day);
                    const isBusy = editedBusySlots[dateKey] && editedBusySlots[dateKey].includes(hour);
                    return (
                      <div
                        key={`${dateKey}-${hour}`}
                        className={`ac-slot ${isBusy ? "busy" : "free"}`}
                        onClick={() => handleSlotClick(dateKey, hour)}
                      ></div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminPanel;